<script lang="ts" setup>
    import '../../../css/choice.css';
    import { onMounted, ref, reactive, computed } from 'vue';
    import { initPinia } from '../../lib/PiniaMgr';
    import { usePrivilegesStore } from '../../lib/Stores/PrivilegesStore' 
    import { AppScreens, PortalType, CompanySettingParameterNames, DatabaseIdTypes } from "../../Enums/ChoiceEnums";
    import { LayoutOrientation, ModalSize } from "../../Enums/UiEnums";
    import { CheckInOutDashboardTab, ICheckInOutDashboardSelectedRow } from '../../lib/Models/ScheduleModel';
    import { IToolbar, ToolbarStyle, ToolbarSize, ToolbarBackground, ToolbarPadding } from "../../lib/Toolbar";
    import { ActionButtonStyle } from "../../lib/Types/Types";
    import { openWindowFromVue } from '../../lib/Models/CommonItemsModel';    
    import { getCompanySetting } from '../../lib/Api/Claim';
    import ResizeMonitor from '../../components/ResizeMonitor.vue'
    import Tab from "../../components/Tab.vue"
    import CheckInOut from '../../components/Schedule/CheckInOut.vue';
    import QuickPay from '../../components/Schedule/QuickPay.vue';
    import PatientHeader from '../../components/Choice/Patient/PatientHeader.vue'
    import Toolbar from "../../components/Toolbar.vue"
    import ChoiceStateMessageSink from '../../components/ChoiceStateMessageSink.vue'
    import CheckInOutTabSetup from '../../components/Schedule/CheckInOutTabSetup.vue';
    import PatientBalance from '../../components/Choice/Patient/PatientBalance.vue';
    import Currency from '../../components/Formatting/Currency.vue';
    import ModalDialog from '../../components/ModalDialog.vue'
    import { ChoiceApi } from '../../LibMain';

    const windowHeight = ref<number>();
    const privilegesStore = usePrivilegesStore(initPinia());

    const QuickPayCheckIn = ref();
    const QuickPayCheckOut = ref();
    const CheckIn = ref();
    const CheckOut = ref();
    const PatientHeaderCheckIn = ref();
    const PatientHeaderCheckOut = ref();
    const CurrentPatientBalance = ref();

    const emit = defineEmits<{
        (e: "onChangeTab", selectedTab: any):void,
        (e: "onChangeCheckInPatient", patientId: number):void,
        (e: "onChangeCheckOutPatient", patientId: number):void,
    }>();

    const props = defineProps<{
        scheduleDate: string
    }>();

    const formData = reactive<{
        tabId: CheckInOutDashboardTab,
        selectedCheckIn?: ICheckInOutDashboardSelectedRow,
        selectedCheckOut?: ICheckInOutDashboardSelectedRow,
        showSetup: boolean,
        keySetup: number,
        keyQuickPayCheckIn: number,
        keyQuickPayCheckOut: number,
        multipleCheckIn: boolean,
        rescheduleCopy: boolean
        showBalance: boolean,
        balanceCheckIn?: number,
        balanceCheckOut?: number,
        useOldIntakeAssign: boolean,
        useOldPatientAppointments: boolean,
    }>({
        tabId: CheckInOutDashboardTab.CheckIn,
        showSetup: false,
        keySetup: 0,
        keyQuickPayCheckIn: 0,
        keyQuickPayCheckOut: 0,
        multipleCheckIn: false,
        rescheduleCopy: false,
        showBalance: false,
        useOldIntakeAssign: true,
        useOldPatientAppointments: true
    });

    enum actionToolbar {
        Patient,
        PrimaryInsuranceCard,
        SecondaryInsuranceCard,
        PatientPhotograph,
        QuickScan,
        Separator,
        SendCommunication,
        PatientSetup,
        PatientHistory,
        PatientCommunications,
        PatientComments,
        PatientAppointments,
        PatientCareTeam,
        OpenPatientEMR,
        OpenPatientEDocuments,
        OpenPatientBilling,
        TemplateEntity,
        PatientEligibility,
        CostEstimator,
        AddToRecallList,
        MMLAdmin,
        IntakeAssignment,
        AppointmentAudit,
        StatementDetails,
        PrintSuperbill,
        Setting
    }
    const toolbarConfig = reactive<IToolbar>({
        toolbarList: [
            {identifier: actionToolbar.Patient, label: "Patient", menuList: [
                {identifier: actionToolbar.PrimaryInsuranceCard, label: "Primary Insurance Card", enabled: privilegesStore.screensWithReadPrivileges[AppScreens.Documents]},
                {identifier: actionToolbar.SecondaryInsuranceCard, label: "Secondary Insurance Card", enabled: privilegesStore.screensWithReadPrivileges[AppScreens.Documents]},
                {identifier: actionToolbar.PatientPhotograph, label: "Patient Photograph", enabled: privilegesStore.screensWithReadPrivileges[AppScreens.Documents]},
                {identifier: actionToolbar.QuickScan, label: "Quick Scan", enabled: privilegesStore.screensWithWritePrivileges[AppScreens.Documents]},
                {identifier: actionToolbar.Separator, label: "-", enabled: false},
                {identifier: actionToolbar.SendCommunication, label: "Send Communication", enabled: true},
                {identifier: actionToolbar.PatientSetup, label: "Patient Setup", enabled: privilegesStore.screensWithReadPrivileges[AppScreens.PatientSetup]},
                {identifier: actionToolbar.PatientHistory, label: "Patient History", enabled: privilegesStore.screensWithReadPrivileges[AppScreens.TransactionHistory]},
                {identifier: actionToolbar.PatientCommunications, label: "Patient Communications", enabled: privilegesStore.screensWithReadPrivileges[AppScreens.PatientCommunications]},
                {identifier: actionToolbar.PatientComments, label: "Patient Comments", enabled: privilegesStore.screensWithReadPrivileges[AppScreens.CommentScreen]},
                {identifier: actionToolbar.PatientAppointments, label: "Patient Appointments", enabled: privilegesStore.screensWithReadPrivileges[AppScreens.PatientAppointments]},
                {identifier: actionToolbar.PatientCareTeam, label: "Care Team", enabled: privilegesStore.screensWithReadPrivileges[AppScreens.PatientCareTeam]},
                {identifier: actionToolbar.OpenPatientEMR, label: "Open Patient in EMR", enabled: privilegesStore.screensWithReadPrivileges[AppScreens.EMRPortal]},
                {identifier: actionToolbar.OpenPatientEDocuments, label: "Open Patient in eDocuments", enabled: privilegesStore.screensWithReadPrivileges[AppScreens.Documents]},
                {identifier: actionToolbar.OpenPatientBilling, label: "Open Patient in Billing", enabled: privilegesStore.screensWithReadPrivileges[AppScreens.BillPortal]},
                {identifier: actionToolbar.TemplateEntity, label: "*** Extension", enabled: privilegesStore.screensWithReadPrivileges[AppScreens.TemplateEntity]},
                {identifier: actionToolbar.PatientEligibility, label: "Eligibility", enabled: privilegesStore.screensWithReadPrivileges[AppScreens.PatientEligibility]},
                {identifier: actionToolbar.CostEstimator, label: "Cost Estimator", enabled: privilegesStore.screensWithReadPrivileges[AppScreens.PatientCostEstimator]},
                {identifier: actionToolbar.AddToRecallList, label: "Add to Recall List", enabled: privilegesStore.screensWithReadPrivileges[AppScreens.RecallSetup]},
                {identifier: actionToolbar.MMLAdmin, label: "MyMedicalLocker Admin", enabled: privilegesStore.screensWithReadPrivileges[AppScreens.MMLAdmin]},
                {identifier: actionToolbar.IntakeAssignment, label: "Intake Assignment", enabled: privilegesStore.screensWithReadPrivileges[AppScreens.IntakeAssignment]},
                {identifier: actionToolbar.AppointmentAudit, label: "Appointment Audit", enabled: privilegesStore.screensWithReadPrivileges[AppScreens.AppointmentAudit]},
                {identifier: actionToolbar.StatementDetails, label: "Quick Statement", enabled: privilegesStore.screensWithReadPrivileges[AppScreens.StatementDetails]},
                {identifier: actionToolbar.Separator, label: "-", enabled: false},
                {identifier: actionToolbar.PrintSuperbill, label: "Print Superbill", enabled: true},                
            ]}
        ]
    });

    const settingConfig = reactive<IToolbar>({
        toolbarList: [
            {identifier: "", label: "", tooltip: "Refresh the tab notifications (icons)",  action: ActionButtonStyle.Refresh, performAction: () => onRefreshTabs()},
            {identifier: "", label: "", tooltip: "Open the section settings",  action: ActionButtonStyle.Setting, performAction: () => onShowTabSetup()},
        ]
    }); 

    async function fetchData(){
        const promisePrivilege = privilegesStore.getScreens([AppScreens.CheckInOutDashboard, AppScreens.PatientSetup, AppScreens.Documents, AppScreens.TransactionHistory, AppScreens.PatientCommunications,
            AppScreens.CommentScreen, AppScreens.PatientAppointments, AppScreens.PatientEligibility, AppScreens.RecallSetup, AppScreens.MMLAdmin, AppScreens.IntakeAssignment,
            AppScreens.AppointmentAudit, AppScreens.StatementDetails, AppScreens.EMRPortal, AppScreens.BillPortal, AppScreens.TemplateEntity, AppScreens.PatientCostEstimator,
            AppScreens.PatientCareTeam
        ]);
        const promiseUseOldIntakeCode = ChoiceApi.user.useScreen('IntakeAssign');
        const promiseTabName = getCompanySetting(CompanySettingParameterNames.EMRTabs,2);
        const promiseMultipleCheckIn = getCompanySetting(CompanySettingParameterNames.Scheduler,41);
        const promiseRescheduleCopy = getCompanySetting(CompanySettingParameterNames.Scheduler,42);
        const promiseuseOldPatientAppointments = ChoiceApi.user.useScreen('PatientAppointments');

        await Promise.all([promisePrivilege, promiseTabName, promiseMultipleCheckIn, promiseRescheduleCopy, promiseUseOldIntakeCode, promiseuseOldPatientAppointments]).then(function(data) {
            setTemplateMenuLabel(actionToolbar.TemplateEntity, data[1]);
            formData.multipleCheckIn = data[2] == "true";
            formData.rescheduleCopy = data[3] == "true";
            formData.useOldIntakeAssign = data[4]?.use ?? true;
            formData.useOldPatientAppointments = data[5]?.use ?? true;
        });
    }
    fetchData();

    onMounted(async () => {
        windowResize();
    });

    const checkInScheduleAppointmentTitle = computed<string>(()=> {
        if (!formData?.selectedCheckIn?.appointmentDate) {
            return "";
        } 
        return `Appt ${formData.selectedCheckIn?.appointmentDate ?? ""} with ${formData.selectedCheckIn?.resourceName}${formData.selectedCheckIn?.serviceLocationName ? " at " + formData.selectedCheckIn?.serviceLocationName : ""}`;
    });  

    const checkOutScheduleAppointmentTitle = computed<string>(()=> {
        if (!formData?.selectedCheckOut?.appointmentDate) {
            return "";
        } 
        return `Appt ${formData.selectedCheckOut?.appointmentDate ?? ""} with ${formData.selectedCheckOut?.resourceName}${formData.selectedCheckOut?.serviceLocationName ? " at " + formData.selectedCheckOut?.serviceLocationName : ""}`;
    });  

    const displayToolbar = computed<boolean>(()=> {
        if (formData.tabId == CheckInOutDashboardTab.CheckIn && !formData?.selectedCheckIn?.patientId)
            return false;
        if (formData.tabId == CheckInOutDashboardTab.CheckOut && !formData?.selectedCheckOut?.patientId)
            return false;
        return true;
    });

    const tabTitle = computed<string>(() => formData.tabId == CheckInOutDashboardTab.CheckIn ? "Check In" : "Check Out");
    const balancePatientId = computed<number | undefined>(() => formData.tabId == CheckInOutDashboardTab.CheckIn ? formData.selectedCheckIn?.patientId : formData.selectedCheckOut?.patientId);
    const balancePatientName = computed<string | undefined>(() => formData.tabId == CheckInOutDashboardTab.CheckIn ? formData.selectedCheckIn?.patientName : formData.selectedCheckOut?.patientName);

    function windowResize() {
        windowHeight.value = window.innerHeight - 230;
    }

    function setTemplateMenuLabel(menuItem: actionToolbar, label: string) {
        const toolbarItem = toolbarConfig.toolbarList[actionToolbar.Patient];
        if (!("menuList" in toolbarItem))
            return;
        const index = toolbarItem.menuList.findIndex(o => o.identifier == menuItem);
        if (index < 0)
            return;
        toolbarItem.menuList[index].label = label;
    }

    function onChangeCheckIn(selectedItem : ICheckInOutDashboardSelectedRow) : void {
        formData.balanceCheckIn = undefined;
        formData.selectedCheckIn = selectedItem;
        formData.keyQuickPayCheckIn += 1;
        emit("onChangeCheckInPatient", selectedItem.patientId);
    }   
    function onChangeCheckOut(selectedItem : ICheckInOutDashboardSelectedRow) : void {
        formData.balanceCheckOut = undefined;
        formData.selectedCheckOut = selectedItem;
        formData.keyQuickPayCheckOut += 1;
        emit("onChangeCheckOutPatient", selectedItem.patientId);
    }
    
    function onStatusChange(selectedItem : ICheckInOutDashboardSelectedRow) : void {
        onRefreshTabs();
    }

    function loadCheckInSchedulerAppointment() {
        if (!formData.selectedCheckIn)
            return;

        MessageLib.MainWindow.openSchedule();

        MessageLib.Scheduler.loadPatientResourceDate(
            {patientEid: formData.selectedCheckIn.patientEid, 
             resourceId: formData.selectedCheckIn.resourceId, 
             scheduleDate: formData.selectedCheckIn.appointmentDate.substring(0,10),
             appointmentId: formData.selectedCheckIn.appointmentId }
        );

    }

    function loadPatientBalance() {
        formData.showBalance = true;
    }

    function loadCheckOutSchedulerAppointment() {
        if (!formData.selectedCheckOut)
            return;

        MessageLib.MainWindow.openSchedule();

        MessageLib.Scheduler.loadPatientResourceDate(
            {patientEid: formData.selectedCheckOut.patientEid, 
             resourceId: formData.selectedCheckOut.resourceId, 
             scheduleDate: formData.selectedCheckOut.appointmentDate.substring(0,10),
             appointmentId: formData.selectedCheckOut.appointmentId }
        );
    }
    async function onRefreshTabs() {
        if (formData.tabId == CheckInOutDashboardTab.CheckIn)
            QuickPayCheckIn.value.refreshTabNotification();
        else if (formData.tabId == CheckInOutDashboardTab.CheckOut)
            QuickPayCheckOut.value.refreshTabNotification();
    }
    async function toolbarClick(data: actionToolbar): Promise<void> {
        let patientId;
        let patientEid;
        let appointmentId;
        let patientName;
        let scheduleDate;
        let commentGroupId;
        if (formData.tabId == CheckInOutDashboardTab.CheckIn) {
            patientId = formData?.selectedCheckIn?.patientId;
            patientEid = formData?.selectedCheckIn?.patientEid;
            appointmentId = formData?.selectedCheckIn?.appointmentId;
            patientName = formData?.selectedCheckIn?.patientName;
            scheduleDate = formData?.selectedCheckIn?.appointmentDate.substring(0,10);
            commentGroupId = formData?.selectedCheckIn?.commentGroupId;
        }
        else if (formData.tabId == CheckInOutDashboardTab.CheckOut) {
            patientId = formData?.selectedCheckOut?.patientId;
            patientEid = formData?.selectedCheckOut?.patientEid;
            appointmentId = formData?.selectedCheckOut?.appointmentId;
            patientName = formData?.selectedCheckOut?.patientName;
            scheduleDate = formData?.selectedCheckOut?.appointmentDate.substring(0,10);
            commentGroupId = formData?.selectedCheckOut?.commentGroupId;
        }
        if (!patientId)
            return;
        let windowId;
        let windowUrl;
        let windowSettings;
        switch (data) {
            case actionToolbar.PrimaryInsuranceCard:
                if (privilegesStore.screensWithReadPrivileges[AppScreens.Documents]) {
                    windowId = AppScreens.PrimaryInsuranceCard;
                    windowUrl = `/officemd/screens/ischedule_document.htm?Opener=NM&Type=I1&Title=${patientName}&SUID=0&PID=${patientId}&PEID=${patientEid}`;
                }
                break;
            case actionToolbar.SecondaryInsuranceCard:
                if (privilegesStore.screensWithReadPrivileges[AppScreens.Documents]) {
                    windowId = AppScreens.SecondaryInsuranceCard;
                    windowUrl = `/officemd/screens/ischedule_document.htm?Opener=NM&Type=I2&Title=${patientName}&SUID=0&PID=${patientId}&PEID=${patientEid}`;
                }
                break;
            case actionToolbar.PatientPhotograph:
                if (privilegesStore.screensWithReadPrivileges[AppScreens.Documents]) {
                    windowId = AppScreens.SecondaryInsuranceCard;
                    windowUrl = `/officemd/screens/ischedule_document.htm?Opener=NM&Type=PP&Title=${patientName}&SUID=0&PID=${patientId}&PEID=${patientEid}`;
                }
                break;
            case actionToolbar.Patient:
            case actionToolbar.PatientSetup:
                if (privilegesStore.screensWithReadPrivileges[AppScreens.PatientSetup]) {
                    windowId = AppScreens.PatientSetup;
                    windowUrl = `/officemd/screens/WindowSetup.htm?WT=Patient Setup&Qualifier=patient&WindowID=${patientId}&tab=Demographics`;
                }
                break;
            case actionToolbar.QuickScan:
                if (privilegesStore.screensWithWritePrivileges[AppScreens.Documents]) {
                    windowId = "DocumentScan";
                    windowUrl = `/officemd/document/documentscan/1/${patientEid}/6/0`;
                    windowSettings = { height: 710, width: 1044, noTimestamp: "Y", resize: false, scrollbar: "no" };
                }
                break;
            case actionToolbar.SendCommunication:
                if (document.defaultView?.top?.openMailUsingIframe)
                    document.defaultView.top.openMailUsingIframe(patientEid);
                else if (document.defaultView?.openMailUsingIframe)
                    document.defaultView.openMailUsingIframe(patientEid);
                break;
            case actionToolbar.PatientHistory:
                if (privilegesStore.screensWithReadPrivileges[AppScreens.TransactionHistory]) {
                    windowId = AppScreens.TransactionHistory;
                    windowUrl = `/officemd/screens/transactionHistory.asp?PatientID=${patientEid}`;
                }
                break;
            case actionToolbar.PatientCommunications:
                if (privilegesStore.screensWithReadPrivileges[AppScreens.PatientCommunications]) {
                    windowId = AppScreens.PatientCommunications;
                    windowUrl = `/officemd/screens/PatientCom.asp?EID=${patientEid}`;
                }
                break;
            case actionToolbar.PatientComments:
                if (privilegesStore.screensWithReadPrivileges[AppScreens.CommentScreen]) {
                    windowId = AppScreens.CommentScreen;
                    windowUrl = `/officemd/Comments/CommentsEditor?cgid=${commentGroupId}`;
                }
                break;
            case actionToolbar.PatientAppointments:
                if (privilegesStore.screensWithReadPrivileges[AppScreens.PatientAppointments]) {
                    windowId = AppScreens.PatientAppointments;
                    if (formData.useOldPatientAppointments)
                        windowUrl = `/officemd/screens/AppointmentPatient.asp?PatientEID=${patientEid}`;
                    else
                        windowUrl = `/OfficeMd/App/Schedule/PatientAppointments/${patientEid}`;
                }
                break;
            case actionToolbar.PatientCareTeam:
                if (privilegesStore.screensWithReadPrivileges[AppScreens.PatientCareTeam]) {
                    windowId = AppScreens.PatientCareTeam;
                    windowUrl = `/officemd/screens/PatientCareTeam.htm?PatientEID=${patientEid}`;
                }
                break;
            case actionToolbar.OpenPatientEMR:
                if (privilegesStore.screensWithReadPrivileges[AppScreens.EMRPortal]) {
                    MessageLib.MainWindow.openEmr(
                        {patientEid: patientEid, 
                        scheduleDate: scheduleDate,
                        appointmentId: appointmentId}
                    );
                }
                break;
            case actionToolbar.OpenPatientEDocuments:
                if (privilegesStore.screensWithReadPrivileges[AppScreens.Documents]) {
                    MessageLib.MainWindow.openEdocument(
                        {patientEid: patientEid}
                    );
                }
                break;
            case actionToolbar.OpenPatientBilling:
                if (privilegesStore.screensWithReadPrivileges[AppScreens.BillPortal]) {
                    MessageLib.MainWindow.openBilling(
                        {patientId: patientId, 
                         scheduleDate: scheduleDate,
                         appointmentId: appointmentId}
                    );
                }
                break;
            case actionToolbar.TemplateEntity:
                if (privilegesStore.screensWithReadPrivileges[AppScreens.TemplateEntity]) {
                    windowId = AppScreens.TemplateEntity;
                    windowUrl = `/officemd/screens/TemplateEntity.asp?EntityType=Patient&EID=${patientEid}`;
                }
                break;
            case actionToolbar.PatientEligibility:
                if (privilegesStore.screensWithReadPrivileges[AppScreens.PatientEligibility]) {
                    windowId = AppScreens.PatientEligibility;
                    if (appointmentId)
                        windowUrl = `/officemd/screens/PatientEligibility.asp?EID=${patientEid}&ApptID=${appointmentId}`;
                    else
                        windowUrl = `/officemd/screens/PatientEligibility.asp?EID=${patientEid}`;
                }
                break;
            case actionToolbar.CostEstimator:
                if (privilegesStore.screensWithReadPrivileges[AppScreens.PatientCostEstimator]) {
                    windowId = AppScreens.PatientCostEstimator;
                    if (appointmentId)
                        windowUrl = `/officemd/screens/vue/PatientCostEstimator.htm?PatientId=${patientId}&ApptId=${appointmentId}`
                    else
                        windowUrl = `/officemd/screens/vue/PatientCostEstimator.htm?PatientId=${patientId}&ApptId=0`;
                }
                break;
            case actionToolbar.AddToRecallList:
                if (privilegesStore.screensWithReadPrivileges[AppScreens.RecallSetup]) {
                    windowId = AppScreens.RecallSetup;
                    windowUrl = `/officemd/app/schedule/recallsetup/0/${patientId}`;
                    windowSettings = { noTimestamp: "Y", height: 350 };
                }
                break;
            case actionToolbar.MMLAdmin:
                if (privilegesStore.screensWithReadPrivileges[AppScreens.MMLAdmin]) {
                    windowId = AppScreens.MMLAdmin;
                    windowUrl = `/officemd/app/mml/mmladmin/entity/${patientEid}`;
                }
                break;
            case actionToolbar.IntakeAssignment:
                if (privilegesStore.screensWithReadPrivileges[AppScreens.IntakeAssignment]) {
                    windowId = AppScreens.IntakeAssignment;
                    if(formData.useOldIntakeAssign){
                        windowUrl = `/officemd/screens/IntakeAssign.htm?PID=${patientId}`;
                    }
                    else{
                        windowUrl = `/officemd/app/intake/IntakeAssign/${patientId}`;
                        windowSettings = { resizable: "true", width: 1400, height: 600, noTimestamp: "Y" };
                    }
                }
                break;
            case actionToolbar.AppointmentAudit:
                if (privilegesStore.screensWithReadPrivileges[AppScreens.AppointmentAudit]) {
                    windowId = AppScreens.AppointmentAudit;
                    windowUrl = `/officemd/screens/AppointmentAudit.asp?PID=${patientId}`;
                }
                break;
            case actionToolbar.StatementDetails:
                if (privilegesStore.screensWithReadPrivileges[AppScreens.StatementDetails]) {
                    windowId = AppScreens.StatementDetails;
                    windowUrl = `/officemd/screens/StatementDetails.htm?pat_eid=${patientEid}&rp_eid=0`;
                }
                break;
            case actionToolbar.PrintSuperbill:
                windowId = AppScreens.PrintSuperbill;
                if (!appointmentId)
                    appointmentId=0;
                windowUrl = `/officemd/SuperBillReports/PrintableSuperBill?appointment_id=${appointmentId}&patient_eid=${patientEid}&superbill_id=0&resource_eid=0&sb_date=`;
                break;
        }
        if (!windowSettings)
            windowSettings = { noTimestamp: "Y" };
        if (windowId && windowUrl)
            openWindowFromVue(windowId, windowUrl, windowSettings);
    }

    async function onShowTabSetup() {
        formData.showSetup = true;
        formData.keySetup += 1;
    }

    async function onHideTabSetup() {
        formData.showSetup = false;
    }

    async function onHideBalance() {
        formData.showBalance = false;
    }

    async function changeToSetup() : Promise<void> {
        if (formData.tabId == CheckInOutDashboardTab.CheckIn)
            formData.keyQuickPayCheckIn += 1;
        else if (formData.tabId == CheckInOutDashboardTab.CheckOut)
            formData.keyQuickPayCheckOut += 1;
    }

    function onChangeTab(tabId : any) {
        if (tabId == CheckInOutDashboardTab.CheckIn)
            CheckIn.value.refreshNow();
        else if (tabId == CheckInOutDashboardTab.CheckOut)
            CheckOut.value.refreshNow();
        emit("onChangeTab", tabId);
    }

    function onOptOutCheckIn() {
        PatientHeaderCheckIn.value.refreshMMLConnection();
    }
    function onOptOutCheckOut() {
        PatientHeaderCheckOut.value.refreshMMLConnection();
    }

    function onRefreshBalance() {
        CurrentPatientBalance.value.refresh();
    }

    function loadBalance(strValue: number) {
        if (formData.tabId == CheckInOutDashboardTab.CheckIn)
            formData.balanceCheckIn = strValue;
        else if (formData.tabId == CheckInOutDashboardTab.CheckOut) 
            formData.balanceCheckOut = strValue;
    }

</script>

<template>
    <ResizeMonitor @resize="windowResize" />
    <div class="row">
        <div class="col-5">
            <div class="row">
                <div class="col">
                    <Tab 
                        :value="CheckInOutDashboardTab.CheckIn" 
                        v-model="formData.tabId" 
                        :layout="LayoutOrientation.Horz"
                        @onChange="onChangeTab">                    
                        Check In
                    </Tab>
                    <Tab 
                        :value="CheckInOutDashboardTab.CheckOut" 
                        v-model="formData.tabId" 
                        :layout="LayoutOrientation.Horz"
                        @onChange="onChangeTab">                    
                        Check Out
                    </Tab>
                </div>
            </div>
            <div class="row" v-show="formData.tabId==CheckInOutDashboardTab.CheckIn">
                <div class="col">
                    <CheckInOut
                        ref="CheckIn"
                        :tabId="CheckInOutDashboardTab.CheckIn"
                        :scheduleDate="$props.scheduleDate"
                        :multipleCheckIn="formData.multipleCheckIn"
                        :rescheduleCopy="formData.rescheduleCopy"
                        @onChange="onChangeCheckIn"
                        @onStatusChange="onStatusChange" />
                </div>
            </div>
            <div class="row" v-show="formData.tabId==CheckInOutDashboardTab.CheckOut">
                <div class="col">
                    <CheckInOut
                        ref="CheckOut"
                        :tabId="CheckInOutDashboardTab.CheckOut"
                        :scheduleDate="$props.scheduleDate"
                        :multipleCheckIn="formData.multipleCheckIn"
                        :rescheduleCopy="formData.rescheduleCopy"
                        @onChange="onChangeCheckOut"
                        @onStatusChange="onStatusChange" />
                </div>
            </div>
        </div>
        <div class="col">
            <div class="row">
                <div class="col-auto" v-if="displayToolbar">
                    <Toolbar 
                        :configuration="toolbarConfig" 
                        :toolbarSize="ToolbarSize.small" 
                        :toolbarStyle="ToolbarStyle.outline" 
                        :toolbarBackground="ToolbarBackground.white"
                        :toolbarPadding="ToolbarPadding.paddingOnlyLeftRight"
                        menuStyle="width: 190px"
                        @toolbarClick="toolbarClick"/>                    
                </div>
                <div class="col mt-1" v-show="formData.tabId==CheckInOutDashboardTab.CheckIn">
                    <div v-if="formData.selectedCheckIn?.patientEid">
                        <PatientHeader 
                            ref="PatientHeaderCheckIn"
                            :key="formData.keyQuickPayCheckIn"
                            :displayContext="PortalType.Scheduler"
                            :legacyPage="false"
                            :patientEntityId="formData.selectedCheckIn?.patientEid" 
                            :appointmentId="formData.selectedCheckIn?.appointmentId"
                            :cacheHeader="false"
                            fontSize="smaller" />
                    </div>                  
                    <div v-if="!formData.selectedCheckIn?.patientEid" class="text-end fst-italic">
                        Please select a patient to continue
                    </div>
                </div>
                <div class="col mt-1" v-show="formData.tabId==CheckInOutDashboardTab.CheckOut">
                    <div v-if="formData.selectedCheckOut?.patientEid">
                        <PatientHeader 
                            ref="PatientHeaderCheckOut"
                            :key="formData.keyQuickPayCheckOut"
                            :displayContext="PortalType.Scheduler"
                            :legacyPage="false"
                            :patientEntityId="formData.selectedCheckOut?.patientEid" 
                            :appointmentId="formData.selectedCheckOut?.appointmentId"
                            :cacheHeader="false"
                            fontSize="smaller" />                  
                    </div>                  
                    <div v-if="!formData.selectedCheckOut?.patientEid" class="text-end fst-italic">
                        Please select a patient to continue
                    </div>
                </div>
            </div>
            <div class="row mt-1">
                <div class="col" v-show="formData.tabId==CheckInOutDashboardTab.CheckIn">
                    <span @click="loadCheckInSchedulerAppointment" class="isalus-link label-isalus-small">
                        {{checkInScheduleAppointmentTitle}}
                    </span>
                </div>
                <div class="col" v-show="formData.tabId==CheckInOutDashboardTab.CheckOut">
                    <span @click="loadCheckOutSchedulerAppointment" class="isalus-link label-isalus-small">
                        {{checkOutScheduleAppointmentTitle}}
                    </span>
                </div>
                <div class="col" v-show="formData.tabId==CheckInOutDashboardTab.CheckIn && formData.selectedCheckIn?.patientName">
                    <span @click="loadPatientBalance" class="isalus-link label-isalus-small">
                        Patient Balance: <Currency :value="formData.balanceCheckIn"/>
                    </span>
                </div>
                <div class="col" v-show="formData.tabId==CheckInOutDashboardTab.CheckOut  && formData.selectedCheckOut?.patientName">
                    <span @click="loadPatientBalance" class="isalus-link label-isalus-small">
                        Patient Balance: <Currency :value="formData.balanceCheckOut"/>
                    </span>
                </div>
                <div class="col-auto">
                    <Toolbar 
                        :configuration="settingConfig" 
                        :toolbarStyle="ToolbarStyle.outline" 
                        :toolbarPadding="ToolbarPadding.paddingOnlyLeftRight"
                        :toolbarBackground="ToolbarBackground.white"/>                      
                </div>            
            </div>
            <div class="row mt-2">
                <div class="col" v-show="formData.tabId==CheckInOutDashboardTab.CheckIn">
                    <QuickPay 
                        ref="QuickPayCheckIn"
                        :key="formData.keyQuickPayCheckIn"
                        :tabId="CheckInOutDashboardTab.CheckIn"
                        :patientId="formData.selectedCheckIn?.patientId ?? 0"
                        :patientEid="formData.selectedCheckIn?.patientEid ?? 0"
                        :appointmentId="formData.selectedCheckIn?.appointmentId"
                        :scheduleDate="formData.selectedCheckIn?.appointmentDate"
                        :appointmentStatusId="formData.selectedCheckIn?.appointmentStatusId"
                        @optOutMML="onOptOutCheckIn" 
                        @refreshBalance="onRefreshBalance" />
                </div>
                <div class="col" v-show="formData.tabId==CheckInOutDashboardTab.CheckOut">
                    <QuickPay 
                        ref="QuickPayCheckOut"
                        :key="formData.keyQuickPayCheckOut"
                        :tabId="CheckInOutDashboardTab.CheckOut"
                        :patientId="formData.selectedCheckOut?.patientId ?? 0"
                        :patientEid="formData.selectedCheckOut?.patientEid ?? 0"
                        :appointmentId="formData.selectedCheckOut?.appointmentId"
                        :scheduleDate="formData.selectedCheckOut?.appointmentDate"
                        :appointmentStatusId="formData.selectedCheckOut?.appointmentStatusId"
                        @optOutMML="onOptOutCheckOut" 
                        @refreshBalance="onRefreshBalance"/>
                </div>
            </div>
        </div>
    </div>
    <ModalDialog :show="formData.showSetup" :size="ModalSize.Large" @hide="onHideTabSetup">
        <template #title>{{tabTitle}} Section Settings</template>
        <CheckInOutTabSetup
            :tabId="formData.tabId"
            :key="formData.keySetup"
            @changeToSetup="changeToSetup">
        </CheckInOutTabSetup>
    </ModalDialog>
    <ModalDialog :show="formData.showBalance" :size="ModalSize.Medium" @hide="onHideBalance">
        <template #title>Patient Balance - {{ balancePatientName }}</template>
        <PatientBalance
            ref="CurrentPatientBalance"
            :databaseIdType="DatabaseIdTypes.patient_id"
            :identifier="balancePatientId"
            @showBalance="loadBalance">
        </PatientBalance>
    </ModalDialog>
    <ChoiceStateMessageSink />
</template>

